import { useEffect, useMemo, useState } from "react";
import {
  createObjective,
  createRagScore,
  deleteRagScore,
  updateObjective,
  updateRagScore,
} from "../data/api";
import { useData } from "../data/DataContext";
import {
  buildRagIndex,
  getRagScoreFor,
  ragFromCode,
  ragLabel,
  ragToCode,
  reportingYearForDate,
  type Rag,
} from "./ObjectiveCard";
import type {
  Objective,
  ObjectiveFieldUpdate,
  ObjectiveStatus,
} from "../data/types";

type EditProps = {
  mode: "edit";
  objective: Objective;
};

type CreateProps = {
  mode: "create";
  personName: string;
  defaultYear?: number;
  defaultCategory?: string;
};

type Props = (EditProps | CreateProps) & {
  categories: string[];
  onClose: () => void;
  onSaved: () => void | Promise<void>;
};

const STATUSES: ObjectiveStatus[] = ["Draft", "Active", "Deleted"];

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Strip in financial-year order (Apr -> Mar), matching ObjectiveCard's strip.
// Calendar-month indices: Apr (3) ... Mar (2).
const STRIP_MONTH_ORDER: number[] = [3, 4, 5, 6, 7, 8, 9, 10, 11, 0, 1, 2];

// Calendar year for a strip cell given the reporting (FY) year.
// Apr-Dec sit in the FY year itself; Jan-Mar sit in FY year + 1.
function calendarYearForCalMonth(calMonthIdx: number, reportingYear: number): number {
  return calMonthIdx >= 3 ? reportingYear : reportingYear + 1;
}

function shortYearLabel(calMonthIdx: number, reportingYear: number): string {
  return String(calendarYearForCalMonth(calMonthIdx, reportingYear) % 100).padStart(2, "0");
}

function ragCellBg(r: Rag): string {
  if (r === "G") return "bg-rag-green";
  if (r === "A") return "bg-rag-amber";
  if (r === "R") return "bg-rag-red";
  return "bg-rag-unset opacity-40";
}

export function ObjectiveModal(props: Props) {
  const initial = useMemo(() => {
    if (props.mode === "edit") {
      return {
        category: props.objective.category ?? "",
        year: props.objective.year != null ? String(props.objective.year) : "",
        status: (props.objective.status ?? "Draft") as ObjectiveStatus,
        objectiveText: props.objective.objectiveText ?? "",
        measuresText: props.objective.measuresText ?? "",
      };
    }
    return {
      category: props.defaultCategory ?? "",
      year: String(props.defaultYear ?? new Date().getFullYear()),
      status: "Draft" as ObjectiveStatus,
      objectiveText: "",
      measuresText: "",
    };
  }, [props]);

  const [category, setCategory] = useState(initial.category);
  const [year, setYear] = useState(initial.year);
  const [status, setStatus] = useState<ObjectiveStatus>(initial.status);
  const [objectiveText, setObjectiveText] = useState(initial.objectiveText);
  const [measuresText, setMeasuresText] = useState(initial.measuresText);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* ---------- scoring state (edit mode only) ---------- */

  const { ragScores, monthLocks, refresh } = useData();

  const scoreYear =
    props.mode === "edit" ? props.objective.year ?? reportingYearForDate() : null;
  const scorableCategory =
    props.mode === "edit" ? props.objective.category : null;
  const scorablePerson =
    props.mode === "edit" ? props.objective.personName : null;
  const canScore =
    props.mode === "edit" &&
    scoreYear != null &&
    scorableCategory != null &&
    scorablePerson != null;

  const ragIndex = useMemo(() => {
    if (!canScore) return null;
    return buildRagIndex(ragScores, scoreYear as number);
  }, [canScore, ragScores, scoreYear]);

  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [panelRag, setPanelRag] = useState<Rag>(null);
  const [panelNarrative, setPanelNarrative] = useState("");
  const [savingScore, setSavingScore] = useState(false);
  const [scoreError, setScoreError] = useState<string | null>(null);
  const [scoreSaved, setScoreSaved] = useState(false);

  const [showJournal, setShowJournal] = useState(false);

  const journalEntries = useMemo(() => {
    if (!canScore || ragIndex == null) return [];
    const entries: Array<{ month: number; rag: Rag; narrative: string }> = [];
    for (let m = 1; m <= 12; m++) {
      const score = getRagScoreFor(
        scorablePerson as string,
        scorableCategory as string,
        m,
        ragIndex,
      );
      if (score && score.narrative && score.narrative.trim().length > 0) {
        entries.push({
          month: m,
          rag: ragFromCode(score.ragValue),
          narrative: score.narrative.trim(),
        });
      }
    }
    return entries;
  }, [canScore, ragIndex, scorablePerson, scorableCategory]);

  const selectedScore = useMemo(() => {
    if (!canScore || ragIndex == null || selectedMonth == null) return null;
    return getRagScoreFor(
      scorablePerson as string,
      scorableCategory as string,
      selectedMonth,
      ragIndex,
    );
  }, [canScore, ragIndex, scorablePerson, scorableCategory, selectedMonth]);

  // Is the currently-selected month locked for this person? When locked,
  // we refuse to save or clear scores against it client-side and show a
  // banner explaining why. The server enforces the same via 423 LOCK_RESPONSE
  // in case the client is stale or hostile (queue item 4 Phase B).
  const isSelectedMonthLocked = useMemo(() => {
    if (!canScore || selectedMonth == null) return false;
    return monthLocks.some(
      (l) =>
        l.personName === scorablePerson &&
        l.year === scoreYear &&
        l.month === selectedMonth,
    );
  }, [canScore, monthLocks, scorablePerson, scoreYear, selectedMonth]);

  // When a new month is selected, prefill the panel from any existing score.
  useEffect(() => {
    if (selectedMonth == null) return;
    setPanelRag(selectedScore ? ragFromCode(selectedScore.ragValue) : null);
    setPanelNarrative(selectedScore?.narrative ?? "");
    setScoreError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);

  const handleSelectMonth = (m: number) => {
    if (savingScore) return;
    setSelectedMonth((prev) => (prev === m ? null : m));
  };

  const handleSaveScore = async () => {
    if (!canScore || selectedMonth == null) return;
    if (isSelectedMonthLocked) {
      setScoreError(
        "This month is locked - unlock it on My Objectives before making changes",
      );
      return;
    }
    setScoreError(null);
    setScoreSaved(false);
    setSavingScore(true);
    try {
      const ragCode = ragToCode(panelRag);
      const narrative = panelNarrative.trim() ? panelNarrative : null;
      if (selectedScore) {
        await updateRagScore(selectedScore.id, {
          RagValue: ragCode,
          Narrative: narrative,
        });
      } else {
        await createRagScore({
          PersonName: scorablePerson as string,
          Category: scorableCategory as string,
          Year: scoreYear as number,
          Month: selectedMonth,
          RagValue: ragCode,
          Narrative: narrative,
        });
      }
      await refresh();
      setScoreSaved(true);
    } catch (err) {
      setScoreError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingScore(false);
    }
  };

  const handleClearScore = async () => {
    if (!selectedScore) {
      setPanelRag(null);
      setPanelNarrative("");
      return;
    }
    if (isSelectedMonthLocked) {
      setScoreError(
        "This month is locked - unlock it on My Objectives before making changes",
      );
      return;
    }
    setScoreError(null);
    setSavingScore(true);
    try {
      await deleteRagScore(selectedScore.id);
      await refresh();
      setPanelRag(null);
      setPanelNarrative("");
    } catch (err) {
      setScoreError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingScore(false);
    }
  };

  const personName = props.mode === "edit" ? props.objective.personName : props.personName;
  const title = props.mode === "edit" ? "Edit objective" : "New objective";
  const saveLabel = props.mode === "edit" ? "Save changes" : "Create";

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) props.onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [props, saving]);

  const dirty =
    category !== initial.category ||
    year !== initial.year ||
    status !== initial.status ||
    objectiveText !== initial.objectiveText ||
    measuresText !== initial.measuresText;

  const canSave =
    !saving &&
    dirty &&
    (props.mode === "edit" || objectiveText.trim().length > 0);

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      const payload: ObjectiveFieldUpdate = {
        Category: category || null,
        Year: year ? Number(year) : null,
        Status: status,
        ObjectiveText: objectiveText,
        MeasuresText: measuresText,
      };
      if (props.mode === "edit") {
        await updateObjective(props.objective.id, payload);
      } else {
        await createObjective({ PersonName: props.personName, ...payload });
      }
      await props.onSaved();
      props.onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={() => !saving && props.onClose()}
    >
      <div
        className={`flex items-start justify-center gap-3 ${
          showJournal && props.mode === "edit" ? "w-full max-w-5xl" : ""
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-lg bg-white shadow-xl">
          <div className="flex items-start justify-between border-b border-slate-200 px-5 py-3">
            <div>
              <h2 className="text-base font-semibold text-slate-900">{title}</h2>
              <p className="text-xs text-slate-500">{personName}</p>
            </div>
            {props.mode === "edit" && canScore ? (
              <button
                type="button"
                onClick={() => setShowJournal((v) => !v)}
                className={[
                  "shrink-0 rounded-md border px-2.5 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.12em] transition",
                  showJournal
                    ? "border-nymr-navy bg-nymr-navy text-white"
                    : "border-rule-strong bg-white text-ink-2 hover:border-ink-3 hover:text-ink",
                ].join(" ")}
              >
                Journal History
              </button>
            ) : null}
          </div>

          <div className="max-h-[calc(90vh-140px)] overflow-y-auto px-5 py-4">
            {canScore && ragIndex != null ? (
              <div className="mb-5 rounded-md border border-rule bg-paper-sunk/50 p-3">
                <div className="mb-2 flex items-baseline justify-between">
                  <span className="font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-brass">
                    Monthly RAG · {scoreYear}/{((scoreYear as number) + 1) % 100}
                  </span>
                  <span className="font-mono text-[9.5px] uppercase tracking-[0.08em] text-ink-3">
                    Click a month to score
                  </span>
                </div>
                <div className="grid grid-cols-12 gap-[3px]">
                  {STRIP_MONTH_ORDER.map((calMonthIdx) => {
                    const m = calMonthIdx + 1;
                    const stored = getRagScoreFor(
                      scorablePerson as string,
                      scorableCategory as string,
                      m,
                      ragIndex,
                    );
                    const storedRag: Rag = stored ? ragFromCode(stored.ragValue) : null;
                    const isCurrent = m === new Date().getMonth() + 1;
                    const isSelected = selectedMonth === m;
                    const cellCalYear = calendarYearForCalMonth(calMonthIdx, scoreYear as number);
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => handleSelectMonth(m)}
                        disabled={savingScore}
                        title={`${MONTH_LONG[calMonthIdx]} ${cellCalYear} · ${ragLabel(storedRag)}`}
                        className={[
                          "flex flex-col items-center gap-[3px] rounded-sm border bg-white p-1 transition",
                          isSelected
                            ? "border-nymr-navy ring-1 ring-nymr-navy"
                            : "border-rule hover:border-rule-strong",
                          isCurrent && !isSelected ? "ring-1 ring-brass" : "",
                          savingScore ? "opacity-60" : "",
                        ].join(" ")}
                      >
                        <span
                          className={[
                            "h-[14px] w-full rounded-[2px]",
                            ragCellBg(storedRag),
                          ].join(" ")}
                        />
                        <span className="font-mono text-[8.5px] uppercase tracking-[0.04em] text-ink-3">
                          {MONTH_SHORT[calMonthIdx]} {shortYearLabel(calMonthIdx, scoreYear as number)}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {selectedMonth != null ? (
                  <div className="mt-3 rounded-md border border-rule bg-card p-3">
                    <div className="mb-3 flex items-baseline justify-between">
                      <div>
                        <div className="font-display text-[15px] font-medium leading-tight text-ink">
                          {MONTH_LONG[selectedMonth - 1]} {calendarYearForCalMonth(selectedMonth - 1, scoreYear as number)}
                        </div>
                        <div className="mt-[2px] font-mono text-[9.5px] uppercase tracking-[0.1em] text-ink-3">
                          {isSelectedMonthLocked
                            ? "Locked · read-only"
                            : selectedScore
                              ? "Existing score · edit and save"
                              : "No score yet · pick a RAG to add one"}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedMonth(null)}
                        disabled={savingScore}
                        className="rounded-sm border border-rule-strong bg-white px-2 py-0.5 text-[11px] text-ink-3 transition hover:text-ink disabled:opacity-50"
                      >
                        Close
                      </button>
                    </div>

                    {isSelectedMonthLocked ? (
                      <div className="flex items-start gap-2 rounded-md border border-brass bg-[rgb(185_138_58/0.10)] px-3 py-2 text-[11.5px] text-brass">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="mt-[1px] shrink-0">
                          <rect x="4" y="11" width="16" height="10" rx="2" />
                          <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                        </svg>
                        <span>
                          <span className="font-medium">{MONTH_LONG[selectedMonth - 1]} {calendarYearForCalMonth(selectedMonth - 1, scoreYear as number)} is locked.</span>{" "}
                          Unlock it from the pill at the top of My Objectives before making changes.
                        </span>
                      </div>
                    ) : (
                      <>
                        <div className="mb-3 grid grid-cols-3 gap-2">
                          {(
                            [
                              { code: "R" as const, label: "Red", bg: "bg-rag-red" },
                              { code: "A" as const, label: "Amber", bg: "bg-rag-amber" },
                              { code: "G" as const, label: "Green", bg: "bg-rag-green" },
                            ]
                          ).map(({ code, label, bg }) => {
                            const selected = panelRag === code;
                            return (
                              <button
                                key={code}
                                type="button"
                                onClick={() => setPanelRag(code)}
                                disabled={savingScore}
                                className={[
                                  "rounded-md border py-2 text-sm font-medium transition",
                                  selected
                                    ? `${bg} border-transparent text-white shadow-sm`
                                    : "border-rule-strong bg-white text-ink-2 hover:border-ink-3 hover:text-ink",
                                  savingScore ? "opacity-60" : "",
                                ].join(" ")}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>

                        <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
                          Narrative
                          <textarea
                            value={panelNarrative}
                            onChange={(e) => setPanelNarrative(e.target.value)}
                            disabled={savingScore}
                            rows={3}
                            placeholder="Optional commentary for this month"
                            className="rounded-md border border-slate-300 px-2 py-1.5 font-sans text-sm font-normal text-slate-900"
                          />
                        </label>

                        {scoreError ? (
                          <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-2 text-[11px] text-red-800">
                            <span className="font-medium">Save failed: </span>
                            <span className="font-mono">{scoreError}</span>
                          </div>
                        ) : scoreSaved ? (
                          <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-[11px] text-emerald-800 font-medium">
                            ✓ Score saved
                          </div>
                        ) : null}

                        <div className="mt-3 flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={handleClearScore}
                            disabled={
                              savingScore ||
                              (!selectedScore && panelRag == null && !panelNarrative)
                            }
                            className="rounded-md border border-rule-strong bg-white px-3 py-1.5 text-sm font-medium text-ink-2 transition hover:bg-paper-sunk disabled:opacity-50"
                          >
                            {selectedScore ? "Clear this month" : "Reset"}
                          </button>
                          <button
                            type="button"
                            onClick={handleSaveScore}
                            disabled={savingScore}
                            className="rounded-md bg-nymr-navy px-3 py-1.5 text-sm font-medium text-white transition hover:bg-nymr-navy-dark disabled:opacity-50"
                          >
                            {savingScore ? "Saving…" : "Save score"}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <label className="flex flex-col gap-1 text-xs font-medium text-slate-500 sm:col-span-2">
                Category
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  disabled={saving}
                  className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm font-normal text-slate-900"
                >
                  <option value="">— none —</option>
                  {props.categories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
                Year
                <input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  disabled={saving}
                  className="rounded-md border border-slate-300 px-2 py-1.5 text-sm font-normal text-slate-900"
                />
              </label>
            </div>

            <label className="mt-4 flex flex-col gap-1 text-xs font-medium text-slate-500">
              Status
              <div className="flex gap-2">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStatus(s)}
                    disabled={saving}
                    className={`rounded-md border px-3 py-1.5 text-sm font-medium transition ${
                      status === s
                        ? s === "Active"
                          ? "border-emerald-600 bg-emerald-600 text-white"
                          : s === "Draft"
                            ? "border-amber-600 bg-amber-600 text-white"
                            : "border-slate-600 bg-slate-600 text-white"
                        : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </label>

            <label className="mt-4 flex flex-col gap-1 text-xs font-medium text-slate-500">
              Objective
              <textarea
                value={objectiveText}
                onChange={(e) => setObjectiveText(e.target.value)}
                disabled={saving}
                rows={6}
                placeholder={props.mode === "create" ? "What needs to be achieved?" : undefined}
                className="rounded-md border border-slate-300 px-2 py-1.5 font-sans text-sm font-normal text-slate-900"
              />
            </label>

            <label className="mt-4 flex flex-col gap-1 text-xs font-medium text-slate-500">
              Measures (Green / Amber / Red)
              <textarea
                value={measuresText}
                onChange={(e) => setMeasuresText(e.target.value)}
                disabled={saving}
                rows={5}
                placeholder={props.mode === "create" ? "Green: …\nAmber: …\nRed: …" : undefined}
                className="rounded-md border border-slate-300 px-2 py-1.5 font-sans text-sm font-normal text-slate-900"
              />
            </label>

            {error && (
              <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                <p className="font-medium">Save failed</p>
                <p className="mt-1 whitespace-pre-wrap font-mono">{error}</p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
            <button
              type="button"
              onClick={props.onClose}
              disabled={saving}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {saving ? "Saving…" : saveLabel}
            </button>
          </div>
        </div>
        {props.mode === "edit" && showJournal && canScore ? (
          <aside className="flex max-h-[90vh] w-[22rem] shrink-0 flex-col overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="flex items-start justify-between border-b border-slate-200 px-5 py-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Journal history</h3>
                <p className="text-xs text-slate-500">
                  {scoreYear}/{((scoreYear as number) + 1) % 100} · {journalEntries.length} entr{journalEntries.length === 1 ? "y" : "ies"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowJournal(false)}
                className="shrink-0 rounded-sm border border-rule-strong bg-white px-2 py-0.5 text-[11px] text-ink-3 transition hover:text-ink"
              >
                Close
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {journalEntries.length === 0 ? (
                <div className="py-6 text-center text-sm text-ink-3">
                  No journal entries recorded yet.
                  <div className="mt-1 text-[11px] text-ink-3/70">
                    Add a narrative when scoring a month and it will appear here.
                  </div>
                </div>
              ) : (
                <ol className="space-y-3">
                  {journalEntries.map((entry) => {
                    const ragBg =
                      entry.rag === "G"
                        ? "bg-rag-green"
                        : entry.rag === "A"
                          ? "bg-rag-amber"
                          : entry.rag === "R"
                            ? "bg-rag-red"
                            : "bg-rag-unset";
                    return (
                      <li
                        key={entry.month}
                        className="rounded-md border border-rule bg-paper-sunk/40 p-3"
                      >
                        <div className="mb-1.5 flex items-center gap-2">
                          <span className={`h-2.5 w-2.5 rounded-sm ${ragBg}`} />
                          <span className="font-display text-[13px] font-medium text-ink">
                            {MONTH_LONG[entry.month - 1]} {calendarYearForCalMonth(entry.month - 1, scoreYear as number)}
                          </span>
                          <span className="ml-auto font-mono text-[9px] uppercase tracking-[0.1em] text-ink-3">
                            {ragLabel(entry.rag)}
                          </span>
                        </div>
                        <p className="whitespace-pre-wrap text-sm leading-snug text-ink-2">
                          {entry.narrative}
                        </p>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </aside>
        ) : null}
      </div>
    </div>
  );
}
